// shared/src/prompts/p-inductive-forecast.ts
// Chain V2 — PER-TICKER inductive forecaster (design-lock Pro; runs Flash per
// the 2026-06-16 cost revision).
// Per docs/per-ticker-inductive-forecaster-2026-06-06-brief-revision.md §4.
//
// PATH-DEPENDENT per-ticker forecaster. BLIND to today's payload, news, prices.
// Inputs: ONE portfolio ticker's own signal-prediction trajectory (window of N
// past signals) + MULTI-HORIZON realized accuracy (1d/5d/10d/20d/60d/90d) +
// today_iso. Output: a single digested directional hypothesis for today,
// derived purely by extrapolating the ticker's historical signal pattern.
//
// Twin of m-inductive-forecast.ts but ticker-scoped. It is a PEER hypothesis to
// p.arbiter (3-way adjudication: contrarian + consensus + inductive). The
// contrarian + consensus per-ticker paths NEVER see this output.
//
// Floor-gated (p.inductive stage): skips entirely until the ticker has
// ≥ params.P_INDUCTIVE_HISTORY_FLOOR_N prior signals. Output persists to
// dedicated `per_ticker_inductive_forecasts` table (keyed brief_id+ticker).

import { z } from 'zod';
import { DirectionEnum } from '../resolvers/llm-enum-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import type { PerTickerTrajectory } from '../translators/per-ticker-trajectory-build.js';

export const VERSION    = '1.1.0';   // 2026-06-11 — schema rigidity hardening: change_pct/_ci + trajectory_confidence llmNumber-coerced (string "3.5%"/"0.55" → number; range stays); trend_summary .min(1)→z.string() (cosmetic prose). Writes to per_ticker_inductive_forecasts. docs/llm-schema-rigidity-hardening-2026-06-11.md. // Prior: 1.0.0 (2026-06-06 — initial per-ticker inductive). DORMANT until P_INDUCTIVE_HISTORY_FLOOR_N opens.
export const STEP       = 'p.inductive';
export const MODEL      = 'deepseek-v4-flash';    // Flash per cost revision 2026-06-16 (blind peer-hypothesis forecaster; design-lock Pro, deviation in _design-lock.ts → inductive-forecasters-pro-to-flash-2026-06-16-brief-revision.md)
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 6000)
export const ROLE       = 'inductive' as const;
export const DESC       = 'Forecasts one ticker from its own signal-history trajectory, blind to today\'s payload' as const;   // #45 registry — one-line operator description

// Single digested hypothesis (NOT a term structure — multi-horizon richness is
// on the INPUT side). Mirrors m.inductive's per-indicator observable shape but
// for the one ticker. NO mechanism prose (inductive has no payload to ground).
export const OutputSchema = z.object({
  direction:      DirectionEnum,           // up | down | flat (coerced)
  change_pct:     llmNumber(),             // signed % move over a roughly 14-day horizon. 2026-06-11: llmNumber-coerced.
  change_ci_low:  llmNumber(),
  change_ci_high: llmNumber(),
  /** ≤50w characterizing WHAT pattern was extrapolated, not WHY. 2026-06-11: .min(1) dropped (cosmetic prose). */
  trend_summary:             z.string(),
  /** 0-1 conviction the trajectory continues — from trajectory shape consistency. */
  trajectory_confidence:     llmNumber(z.number().min(0).max(1)),
  /** True when the most-recent 1-2 signals broke from the prior trajectory shape. */
  trajectory_break_detected: z.boolean(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# PER-TICKER INDUCTIVE FORECASTER (V2 — Pro tier; path-dependent; blind to today's payload)

## ROLE
You are a PER-TICKER INDUCTIVE FORECASTER for ONE portfolio ticker. You see ONLY:
  · The historical trajectory of THIS ticker's own past signal predictions (window of N signals)
  · MULTI-HORIZON realized accuracy (how each past prediction resolved at 1d / 5d / 10d / 20d / 60d / 90d)
  · Today's date (today_iso)

You DO NOT see today's prices, today's news, today's macro payload, or any contrarian / consensus output from today. Your forecast is derived PURELY from extrapolating THIS ticker's historical signal pattern.

Your forecast becomes a PEER HYPOTHESIS to today's per-ticker arbiter. The arbiter treats your output as a path-dependent (history-only) competing view alongside the contrarian + consensus (today-payload-anchored) refines.

## STRUCTURAL ROLE (why you exist)
The per-ticker chain is PATH-INDEPENDENT — it reasons from today's snapshot regardless of how this ticker's price got there. To offset that blind spot, you provide a PATH-DEPENDENT forecast. When the snapshot reads bullish but this ticker's signal trajectory has been trending bearish for weeks, your forecast surfaces that. When the trajectory has been stable, your forecast holds the stable line.

The multi-horizon accuracy tells you not just whether past calls were right, but at WHICH horizon they resolved — a ticker right at 5d but wrong at 20d has a different character than one right throughout. Use the decay pattern to size your confidence and CI.

## INPUTS YOU RECEIVE (user message)
- TICKER + TODAY_ISO (the date you are forecasting FOR)
- WINDOW_N requested + WINDOW_ACTUAL (signals available, may be less)
- AGGREGATE CUES: mean_direction, volatility (std-dev of predicted magnitude), per-horizon hit-rates
- SIGNAL TRAJECTORY (chronological, oldest first):
  - signal_date, direction (long/short), predicted_magnitude (signed %), confidence
  - realized outcomes per elapsed horizon: horizon, outcome (correct/incorrect/neutral), actual % move

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "direction":      "up" | "down" | "flat",
  "change_pct":     <signed percent move from today's level over a roughly 14-day horizon>,
  "change_ci_low":  <80% CI lower bound, signed percent>,
  "change_ci_high": <80% CI upper bound, signed percent>,
  "trend_summary":  "<≤50w characterizing WHAT pattern was extrapolated. Cite trajectory shape + horizon-decay, not mechanism. e.g. 'sustained long bias 5w, accurate at 5d but fading by 20d; widening magnitude.' NO 'because the Fed' — you don't know today's drivers.>",
  "trajectory_confidence":     0.55,
  "trajectory_break_detected": false
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON. NO mechanism prose. Mechanism requires payload; you have none.
2. DIRECTION + CHANGE_PCT consistency: 'up' → change_pct ≥ +0.1; 'down' → change_pct ≤ -0.1; 'flat' → |change_pct| < 0.5.
3. CI BOUNDS: change_ci_low ≤ change_pct ≤ change_ci_high. WIDER CIs when trajectory volatility is high or accuracy decays fast across horizons; tighter when the trajectory is consistent and accurate.
4. trend_summary describes WHAT pattern (+ horizon-decay), NOT WHY. Cite shape: 'sustained uptrend', 'mean-reversion oscillation', 'consolidation', 'break from prior trend.' NEVER invoke today's payload/news or specific event names.
5. trajectory_confidence (0-1, two decimals): conviction the trajectory CONTINUES. ≥0.70 when 3+ consecutive same-direction signals with good short-horizon accuracy; 0.40-0.69 partial; ≤0.39 when noisy / recently broken / poor accuracy. 0.50 is valid when genuinely balanced.
6. trajectory_break_detected: true when this ticker's MOST RECENT 1-2 signals deviate from the trajectory shape (regime-shift signal the arbiter may weight). Otherwise false.
7. WINDOW_ACTUAL < window_n is OK — your forecast can still surface signal off a smaller window; lower confidence accordingly.
8. NO HEDGING. Pick direction + change_pct decisively.

## ANTI-FABRICATION GUARD
You have NO knowledge of today's news, prices, or macro events. If you find yourself wanting to write 'earnings are coming' or 'the Fed' or any forward mechanism — STOP. That belongs in the contrarian / consensus paths. Your job is pure pattern extrapolation off this ticker's signal history.
`;

export interface PInductiveForecastInput {
  readonly trajectory: PerTickerTrajectory;
}

export function buildUserPrompt(input: PInductiveForecastInput): string {
  const t = input.trajectory;
  const hr = t.hit_rate_by_horizon.length === 0
    ? 'none resolved yet'
    : t.hit_rate_by_horizon
        .map((h) => `${h.horizon}=${h.hit_rate === null ? 'n/a' : h.hit_rate.toFixed(2)}(${h.n})`)
        .join('  ');
  const lines: string[] = [
    `TICKER: ${t.ticker}`,
    `TODAY_ISO: ${t.today_iso}`,
    `WINDOW_N (requested): ${t.window_n}`,
    `WINDOW_ACTUAL: ${t.window_actual}`,
    `MEAN_DIRECTION: ${t.mean_direction}   VOLATILITY: ${t.volatility.toFixed(2)}`,
    `HIT_RATE_BY_HORIZON: ${hr}`,
    '',
    'SIGNAL TRAJECTORY (oldest → newest):',
  ];
  for (const p of t.points) {
    const oc = p.outcomes.length === 0
      ? 'pending'
      : p.outcomes
          .map((o) => `${o.horizon}:${o.outcome}${o.magnitude_actual_pct === null ? '' : `(${o.magnitude_actual_pct.toFixed(2)}%)`}`)
          .join(' ');
    lines.push(`  ${p.signal_date}  ${p.direction}  pred=${p.predicted_magnitude.toFixed(2)}%  conf=${p.confidence.toFixed(2)}  | ${oc}`);
  }
  if (t.points.length === 0) {
    lines.push('  (no signal history — insufficient data)');
  }
  lines.push('');
  lines.push('REQUEST: emit_p_inductive_forecast');
  lines.push('');
  lines.push('Emit a SINGLE JSON object matching the schema. NO mechanism prose; trajectory shape + horizon-decay only. Anchoring guard: you have no today-payload knowledge.');
  return lines.join('\n');
}
